/**
 * <bundle-and-save>
 *
 * "Complete Your Stack". A list of product rows; selecting a row expands it to
 * reveal the product image, price, a variant dropdown, and (when the product
 * has selling plans) a subscription toggle + frequency dropdown. A single
 * shared Add to Cart adds the selected row's chosen variant — with the chosen
 * selling plan when subscribed — via the theme's AJAX cart, then opens the
 * cart drawer. The locked-in summary mirrors the selection.
 */
class BundleAndSave extends HTMLElement {
	connectedCallback() {
		this.soldOutText = this.dataset.soldOutText || 'Sold out';
		// Editable savings text; [percent] is replaced with the computed % (e.g. "5%").
		this.subSaveText = this.dataset.subSaveText || 'and save [percent]';
		this.rows = Array.from(this.querySelectorAll('[data-bundle-tier]'));
		if (this.rows.length === 0) {
			return;
		}

		this.rowState = new Map(); // rowKey → { variants, chosenVariantId, plans, subscribe, chosenPlanId }
		this.bound = new WeakMap();
		this.rows.forEach((row) => this.initRow(row));

		this.selectedRow = this.rows.find((r) => r.classList.contains('is-selected')) || this.rows[0];
		this.atcButton = this.querySelector('[data-bundle-atc]');
		this.captureAtcDefaultLabel();
		this.syncLockedIn();
		this.syncAtc();
		this.addListeners();
	}

	disconnectedCallback() {
		// Theme-editor reloads re-attach the element; tear listeners down so they don't stack.
		this.rows?.forEach((row) => {
			const handlers = this.bound.get(row);
			if (!handlers) {
				return;
			}
			handlers.radio?.removeEventListener('change', handlers.onSelect);
			handlers.select?.removeEventListener('change', handlers.onVariant);
			handlers.subToggle?.removeEventListener('change', handlers.onSubToggle);
			handlers.planSelect?.removeEventListener('change', handlers.onPlan);
		});
		if (this.boundAtcClick) {
			this.atcButton?.removeEventListener('click', this.boundAtcClick);
		}
	}

	initRow(row) {
		const dataEl = row.querySelector('[data-tier-variants]');
		let data = { variants: [], plans: [], requiresSellingPlan: false };
		try {
			if (dataEl) {
				data = { ...data, ...JSON.parse(dataEl.textContent) };
			}
		} catch (err) {
			console.error('[bundle-and-save] Failed to parse variant JSON', err);
		}
		const select = row.querySelector('[data-tier-select]');
		const subToggle = row.querySelector('[data-tier-sub-toggle]');
		const planSelect = row.querySelector('[data-tier-plan-select]');

		let chosenVariantId;
		if (select) {
			chosenVariantId = Number(select.value);
		} else {
			const first = data.variants.find((v) => v.available) || data.variants[0];
			chosenVariantId = first?.id ?? null;
		}
		this.rowState.set(row.dataset.rowKey, {
			variants: data.variants,
			plans: data.plans,
			chosenVariantId,
			subscribe: !!subToggle?.checked,
			chosenPlanId: planSelect ? Number(planSelect.value) : null,
		});
		// SSR renders one-time pricing; re-render rows that hydrate subscribed (requires_selling_plan).
		if (subToggle?.checked) {
			this.renderRowPrice(row);
			this.updateSaveLabel(row);
		}
		this.updateSubscriptionUI(row);
	}

	addListeners() {
		this.rows.forEach((row) => {
			const radio = row.querySelector('[data-bundle-radio]');
			const select = row.querySelector('[data-tier-select]');
			const subToggle = row.querySelector('[data-tier-sub-toggle]');
			const planSelect = row.querySelector('[data-tier-plan-select]');
			const onSelect = () => this.selectRow(row);
			const onVariant = () => this.onVariantChange(row, select);
			const onSubToggle = () => this.onSubToggleChange(row, subToggle);
			const onPlan = () => this.onPlanChange(row, planSelect);
			radio?.addEventListener('change', onSelect);
			select?.addEventListener('change', onVariant);
			subToggle?.addEventListener('change', onSubToggle);
			planSelect?.addEventListener('change', onPlan);
			this.bound.set(row, { radio, select, subToggle, planSelect, onSelect, onVariant, onSubToggle, onPlan });
		});
		this.boundAtcClick = () => this.onAtcClick();
		this.atcButton?.addEventListener('click', this.boundAtcClick);
	}

	selectRow(row) {
		if (!row) {
			return;
		}
		this.rows.forEach((r) => {
			const isTarget = r === row;
			r.classList.toggle('is-selected', isTarget);
			r.classList.toggle('is-expanded', isTarget);
		});
		this.selectedRow = row;
		this.syncLockedIn();
		this.syncAtc();
		this.clearError();
	}

	onVariantChange(row, select) {
		const state = this.rowState.get(row.dataset.rowKey);
		if (!state) {
			return;
		}
		state.chosenVariantId = Number(select.value);
		this.updateSubscriptionUI(row);
		this.renderRowPrice(row);
		this.updateSaveLabel(row);
		if (row === this.selectedRow) {
			this.syncLockedIn();
			this.syncAtc();
		}
		this.clearError();
	}

	onSubToggleChange(row, subToggle) {
		const state = this.rowState.get(row.dataset.rowKey);
		if (!state) {
			return;
		}
		state.subscribe = !!subToggle.checked;
		const frequency = row.querySelector('[data-tier-sub-frequency]');
		if (frequency) {
			frequency.hidden = !state.subscribe;
		}
		this.renderRowPrice(row);
		if (row === this.selectedRow) {
			this.syncLockedIn();
		}
		this.clearError();
	}

	onPlanChange(row, planSelect) {
		const state = this.rowState.get(row.dataset.rowKey);
		if (!state) {
			return;
		}
		state.chosenPlanId = Number(planSelect.value);
		this.renderRowPrice(row);
		this.updateSaveLabel(row);
		if (row === this.selectedRow) {
			this.syncLockedIn();
		}
		this.clearError();
	}

	chosenVariant(state) {
		return state?.variants.find((v) => v.id === state.chosenVariantId) || null;
	}

	// True when the chosen variant has an allocation for at least one offered subscription plan.
	subscriptionAvailable(state, variant) {
		if (!variant || !state?.plans?.length) {
			return false;
		}
		const offered = new Set(state.plans.map((p) => p.id));
		return (variant.allocations || []).some((a) => offered.has(a.plan));
	}

	// Hide the "Upgrade to Subscription" toggle when the chosen variant offers no subscription
	// (inline display beats the section's id-scoped display:flex); fall back to one-time if it was
	// subscribed. onAtcClick's allocation guard stays as a safety net.
	updateSubscriptionUI(row) {
		const wrap = row.querySelector('.bundle-and-save__tier-subscription');
		if (!wrap) {
			return;
		}
		const state = this.rowState.get(row.dataset.rowKey);
		const available = this.subscriptionAvailable(state, this.chosenVariant(state));
		wrap.style.display = available ? '' : 'none';
		if (available || !state.subscribe) {
			return;
		}
		state.subscribe = false;
		const subToggle = row.querySelector('[data-tier-sub-toggle]');
		if (subToggle) {
			subToggle.checked = false;
		}
		const frequency = row.querySelector('[data-tier-sub-frequency]');
		if (frequency) {
			frequency.hidden = true;
		}
		this.renderRowPrice(row);
		this.updateSaveLabel(row);
		if (row === this.selectedRow) {
			this.syncLockedIn();
		}
	}

	// Allocation price for the row's chosen plan, or null when none applies.
	allocationPrice(state, variant) {
		if (!variant || !state?.chosenPlanId) {
			return null;
		}
		const alloc = (variant.allocations || []).find((a) => a.plan === state.chosenPlanId);
		return alloc ? alloc.price : null;
	}

	renderRowPrice(row) {
		const state = this.rowState.get(row.dataset.rowKey);
		const variant = this.chosenVariant(state);
		if (!variant) {
			return;
		}
		const current = row.querySelector('[data-tier-price-current]');
		const pct = row.querySelector('[data-tier-price-pct]');
		const was = row.querySelector('[data-tier-price-was]');

		// Subscribed: allocation price vs one-time price. One-time: price vs compare-at.
		let displayPrice = variant.price;
		let comparePrice = variant.compare_at_price;
		const allocPrice = state.subscribe ? this.allocationPrice(state, variant) : null;
		if (allocPrice !== null) {
			displayPrice = allocPrice;
			comparePrice = variant.price;
		}
		const onSale = comparePrice > displayPrice;

		if (current) {
			current.textContent = this.formatMoney(displayPrice);
		}
		if (pct) {
			// Em-dash placeholder keeps the discount column aligned on non-discounted rows.
			pct.textContent = onSale ? '-' + Math.floor(((comparePrice - displayPrice) * 100) / comparePrice) + '%' : '—';
			pct.classList.toggle('bundle-and-save__tier-price-pct--none', !onSale);
		}
		if (was) {
			was.textContent = onSale ? 'was ' + this.formatMoney(comparePrice) : '';
			was.hidden = !onSale;
		}
	}

	// Savings label on the toggle, from the chosen variant's allocation for the chosen plan.
	updateSaveLabel(row) {
		const saveEl = row.querySelector('[data-tier-sub-save]');
		if (!saveEl) {
			return;
		}
		const state = this.rowState.get(row.dataset.rowKey);
		const variant = this.chosenVariant(state);
		const allocPrice = this.allocationPrice(state, variant);
		const pctValue =
			variant && allocPrice !== null && variant.price > allocPrice
				? Math.floor(((variant.price - allocPrice) * 100) / variant.price)
				: 0;
		saveEl.textContent = pctValue > 0 ? ` ${this.subSaveText.replaceAll('[percent]', `${pctValue}%`)}` : '';
		saveEl.hidden = pctValue === 0;
	}

	syncLockedIn() {
		const titleEl = this.querySelector('[data-lockedin-title]');
		const detailEl = this.querySelector('[data-lockedin-detail]');
		if (!this.selectedRow) {
			return;
		}
		const state = this.rowState.get(this.selectedRow.dataset.rowKey);
		const variant = this.chosenVariant(state);

		if (titleEl) {
			const titleSource = this.selectedRow.querySelector('.bundle-and-save__tier-title');
			titleEl.textContent = titleSource ? titleSource.textContent.trim() : '';
		}
		if (detailEl && variant) {
			const parts = [];
			if ((state?.variants.length || 0) > 1) {
				parts.push(variant.title);
			}
			const allocPrice = state.subscribe ? this.allocationPrice(state, variant) : null;
			parts.push(this.formatMoney(allocPrice !== null ? allocPrice : variant.price));
			if (state.subscribe && state.chosenPlanId) {
				const plan = state.plans.find((p) => p.id === state.chosenPlanId);
				if (plan) {
					parts.push(plan.name);
				}
			}
			detailEl.textContent = parts.join(' · ');
		}
	}

	syncAtc() {
		if (!this.atcButton) {
			return;
		}
		const state = this.rowState.get(this.selectedRow?.dataset.rowKey);
		const variant = this.chosenVariant(state);
		const available = !!variant?.available;
		this.atcButton.disabled = !available;
		const label = this.atcButton.querySelector('[data-bundle-atc-text]');
		if (label) {
			label.textContent = available ? label.dataset.defaultLabel : this.soldOutText;
		}
	}

	captureAtcDefaultLabel() {
		const label = this.atcButton?.querySelector('[data-bundle-atc-text]');
		if (label && !label.dataset.defaultLabel) {
			label.dataset.defaultLabel = label.textContent.trim();
		}
	}

	async onAtcClick() {
		if (!this.atcButton || this.atcButton.disabled || this.atcButton.classList.contains('is-loading')) {
			return;
		}
		const state = this.rowState.get(this.selectedRow?.dataset.rowKey);
		const variantId = state?.chosenVariantId;
		if (!variantId) {
			this.showError('Please choose an option.');
			return;
		}
		this.clearError();
		this.setLoading(true);

		try {
			// Only subscribe when the chosen variant actually has an allocation for the chosen plan.
			// Sending a selling_plan the variant doesn't support makes /cart/add reject the whole add.
			const variant = this.chosenVariant(state);
			const wantsSubscription = !!(state.subscribe && state.chosenPlanId);
			const planValidForVariant =
				wantsSubscription && (variant?.allocations || []).some((a) => a.plan === state.chosenPlanId);
			if (wantsSubscription && !planValidForVariant) {
				this.showError('This option is not available for subscription. Pick another option or uncheck subscription.');
				return;
			}

			const body = new FormData();
			body.append('id', String(variantId));
			body.append('quantity', '1');
			if (planValidForVariant) {
				body.append('selling_plan', String(state.chosenPlanId));
			}

			const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
				method: 'POST',
				headers: {
					'Accept': 'application/javascript',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body,
			});

			const json = await response.json();
			if (!response.ok || json.status) {
				this.showError(json.description || json.message || 'Could not add to cart.');
				return;
			}
			this.openCartDrawer();
		} catch (err) {
			console.error('[bundle-and-save] ATC failed', err);
			this.showError('Could not add to cart. Please try again.');
		} finally {
			this.setLoading(false);
		}
	}

	openCartDrawer() {
		const cartItems = document.querySelector('cart-items');
		const cartDrawer = document.querySelector('#DrawerCart');
		if (cartItems && cartDrawer) {
			// No argument: see product-form.js. rerenderFromFetch refetches the sections
			// itself and now takes an optional focus key, not a response. It rejects on
			// a failed render, and the add has already landed, so fall back to the real
			// cart rather than leaving the drawer on a cart without the new item.
			cartItems.rerenderFromFetch().catch(() => {
				window.location = window.routes?.cart_url || '/cart';
			});
			// UpCart (#upCart app embed) intercepts the add and opens its own drawer; opening the
			// native drawer too stacks a second cart under it. Only open native when UpCart is absent.
			if (!document.getElementById('upCart')) {
				cartDrawer.open();
			}
		} else {
			window.location = window.routes?.cart_url || '/cart';
		}
	}

	setLoading(isLoading) {
		this.atcButton.classList.toggle('is-loading', isLoading);
		this.atcButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
	}

	showError(message) {
		const el = this.querySelector('[data-bundle-error]');
		if (!el) {
			return;
		}
		el.textContent = message;
		el.hidden = false;
	}

	clearError() {
		const el = this.querySelector('[data-bundle-error]');
		if (!el) {
			return;
		}
		el.textContent = '';
		el.hidden = true;
	}

	formatMoney(cents) {
		// Matches the simple `$cents/100` convention used by variant-picker.js.
		return '$' + (cents / 100).toFixed(2);
	}
}

if (!customElements.get('bundle-and-save')) {
	customElements.define('bundle-and-save', BundleAndSave);
}
